import { IkeUnitAngle, IkeUnitDistance, type IkeUnits, IkeUnitType } from './units';
import type { UnitInfo, UnitTypeMap } from './unit.types';

const DECIMAL = /^(-)?((?:[1-9]\d*|0)(?:\.\d*)?|\.\d+)([eE][+-]?\d+)?$/;
const FEET_INCH = /^(-)?(\d+)'(?:[ ]?(\d+)")?$/;
const INCHES_IN_FEET = 12;

/** Utility to help working with Distances and Angles */
export class IkeUnit {
	// Distance
	static FEET: typeof IkeUnitDistance.FEET = IkeUnitDistance.FEET;
	static METER: typeof IkeUnitDistance.METER = IkeUnitDistance.METER;
	static CENTIMETER: typeof IkeUnitDistance.CENTIMETER = IkeUnitDistance.CENTIMETER;
	static MILLIMETER: typeof IkeUnitDistance.MILLIMETER = IkeUnitDistance.MILLIMETER;
	static INCH: typeof IkeUnitDistance.INCH = IkeUnitDistance.INCH;

	// Angles
	static RADIAN: typeof IkeUnitAngle.RADIAN = IkeUnitAngle.RADIAN;
	static DEGREE: typeof IkeUnitAngle.DEGREE = IkeUnitAngle.DEGREE;

	/** Default distance type to use */
	static DEFAULT = IkeUnit.METER;
	/** Default formatting length for distances and plain numbers (UFO-615) */
	static DECIMALS = 2;
	/** Default formatting length for angles, which need finer precision (UFO-121) */
	static ANGLE_DECIMALS = 3;

	static UNITS: UnitTypeMap = {
		[IkeUnitType.ANGLE]: {},
		[IkeUnitType.DISTANCE]: {},
		[IkeUnitType.AREA]: {},
	};
	static DISPLAY_TYPE: UnitTypeMap = {
		[IkeUnitType.ANGLE]: {},
		[IkeUnitType.DISTANCE]: {},
		[IkeUnitType.AREA]: {},
	};

	/** Each unit type has a base type that all values are stored as */
	static BASE_TYPE = {
		[IkeUnitType.ANGLE]: IkeUnitAngle.RADIAN,
		[IkeUnitType.DISTANCE]: IkeUnitDistance.METER,
		[IkeUnitType.AREA]: IkeUnitDistance.METER,
	};

	/**
	 * Case sensitive search for unit type
	 * @see IkeUnit.toUnitDistance
	 */
	static isUnitDistance(unit: string): unit is IkeUnitDistance {
		return IkeUnit.UNITS[IkeUnitType.DISTANCE][unit.toLowerCase()] != null;
	}

	/** Case insensitive unit type search */
	static toUnitDistance(unit: string): IkeUnitDistance | null {
		let searchUnit = unit.toLowerCase();
		if (IkeUnit.isUnitDistance(searchUnit)) {
			return searchUnit;
		}
		return null;
	}

	/** Add an additional unit to IkeUnit */
	static addUnit(unit: UnitInfo) {
		const unitType = unit.type == null ? IkeUnitType.DISTANCE : unit.type;
		IkeUnit.UNITS[unitType][unit.unit] = unit;

		const localDisplay = IkeUnit.DISPLAY_TYPE[unitType];
		for (const displayUnit of unit.display) {
			localDisplay[displayUnit] = unit;
		}
	}

	/** format a angle */
	static displayAngle(value: number, to: IkeUnitAngle, from?: IkeUnitAngle, decimals?: number): string {
		return IkeUnit.display(value, to, from, IkeUnitType.ANGLE, decimals);
	}

	/** Format a area */
	static displayArea(value: number, to: IkeUnitDistance, from?: IkeUnitDistance): string {
		return IkeUnit.display(value, to, from, IkeUnitType.AREA);
	}

	/**
	 * Format a value  into a human readable output
	 * */
	static display(value: number, to: IkeUnits, from?: IkeUnits, unitType?: IkeUnitType, decimals?: number): string {
		if (unitType == null) {
			unitType = IkeUnit.getUnitType(to);
			if (unitType == null) {
				return '';
			}
		}

		if (decimals == null) {
			decimals = unitType === IkeUnitType.ANGLE ? IkeUnit.ANGLE_DECIMALS : IkeUnit.DECIMALS;
		}

		if (from == null) {
			from = IkeUnit.getBaseType(unitType);
		}

		// Convert the value if required into the output value
		const actualValue = IkeUnit.convert(value, to, from, unitType);
		if (isNaN(actualValue)) {
			return '';
		}

		const unitConversions = IkeUnit.UNITS[unitType];
		const toConversion = unitConversions[to];

		// If the convertor provides a formatting function use it. (Feet/Inches)
		if (toConversion.format && unitType === IkeUnitType.DISTANCE) {
			return toConversion.format(actualValue, unitType);
		}

		return IkeUnit.toFixed(actualValue, decimals) + IkeUnit.getDisplay(to, unitType);
	}

	/** Convert a angle from one angle type to another angle type */
	static convertAngle(value: number, to: IkeUnitAngle, from?: IkeUnitAngle): number {
		return IkeUnit.convert(value, to, from, IkeUnitType.ANGLE);
	}

	static convertArea(value: number, to: IkeUnitDistance, from?: IkeUnitDistance): number {
		return IkeUnit.convert(value, to, from, IkeUnitType.AREA);
	}

	static getUnitType(type: IkeUnits) {
		for (const unit of UNITS) {
			if (unit.unit === type) {
				return unit.type;
			}
		}
		return null;
	}

	/**
	 * Convert a number from one unit to another
	 * @param value value to convert
	 * @param to unit type to convert to
	 * @param from unit type to convert from
	 * @param unitType Type of to and from
	 */
	static convert(value: number, to: IkeUnits, from?: IkeUnits, unitType?: IkeUnitType): number {
		if (isNaN(value) || typeof value !== 'number') {
			return NaN;
		}

		// Attempt to determine the base unit type for the to unit type
		if (unitType == null) {
			unitType = IkeUnit.getUnitType(to);
			if (unitType == null) {
				return NaN;
			}
		}

		// Assume the base type if no from is provided
		const baseType = IkeUnit.getBaseType(unitType);
		if (from == null) {
			from = baseType;
		}

		const unitConversions = IkeUnit.UNITS[unitType];
		// If the from is not the base type, convert into the base type first
		if (from !== baseType) {
			var fromConversion = unitConversions[from];
			if (fromConversion == null) {
				return NaN;
			}
			value = value * fromConversion.conversion;
		}

		var toConversion = unitConversions[to];
		if (toConversion == null) {
			return NaN;
		}

		// Convert into the output value
		return value / toConversion.conversion;
	}

	/** Get the display information for the unit, for example " for inches */
	static getDisplay(unit: string, unitType: IkeUnitType = IkeUnitType.DISTANCE): string {
		const unitConversions = IkeUnit.UNITS[unitType];

		const type = unitConversions[unit];
		if (type == null) {
			return '';
		}

		if (Array.isArray(type.display)) {
			return type.display[0];
		}

		return type.display;
	}

	/** Get the base UnitType */
	static getBaseType(unitType: IkeUnitType = IkeUnitType.DISTANCE): IkeUnits {
		return IkeUnit.BASE_TYPE[unitType];
	}

	/**
	 * Convert the string input into Distance
	 *
	 * @param input String value of distance.
	 * @param unit Unit type to parse from.
	 * @returns {number} Distance in Meters.
	 */
	static parse(input: string, unit: string): number {
		if (input == null) {
			return NaN;
		}

		let test = input.trim();
		if (test.length === 0) {
			return NaN;
		}
		let currentUnit, unitPortion;
		const unitMatches = test.match(/[^0-9]+$/);
		if (unitMatches) {
			unitPortion = unitMatches[0];
			var currentUnitObj = IkeUnit.DISPLAY_TYPE[IkeUnitType.DISTANCE][unitPortion];
			if (currentUnitObj != null) {
				currentUnit = currentUnitObj.unit;
			}
		}

		if (currentUnit) {
			test = test.substr(0, test.length - unitPortion.length);
		} else {
			currentUnit = unit;
		}

		const matches = test.match(DECIMAL);
		if (matches) {
			var sign = matches[1] == null ? 1 : -1;
			return sign * IkeUnit.convert(parseFloat(matches[2]), IkeUnit.METER, currentUnit);
		}

		return IkeUnit.parseFeetInch(input);
	}

	static getUnits(unitType: IkeUnitType) {
		return IkeUnit.UNITS[unitType];
	}

	// Better ToFixed implementation
	// @see http://stackoverflow.com/questions/10015027/javascript-tofixed-not-rounding
	static toFixed(num: number, precision: number) {
		return (+(Math.round(+(num + 'e' + precision)) + 'e' + -precision)).toFixed(precision);
	}

	/**
	 * Round a number to the provided precision
	 *
	 * @param num Number to round
	 * @param precision Number of decimal places to keep
	 */
	static round(num: number, precision: number) {
		return Math.round(<any>(num * Math.pow(10, precision)).toFixed(precision - 1)) / Math.pow(10, precision);
	}

	/** Split inches into feet and inches  */
	static splitFeetInch(inches: number) {
		/* eslint-disable no-bitwise */
		return {
			feet: ~~(inches / INCHES_IN_FEET), // Truncate the remainder
			inches: inches % INCHES_IN_FEET,
		};
		/* eslint-enable no-bitwise */
	}

	/**
	 * Join feet & inches into inches
	 * @param feet Number of feet (Feet)
	 * @param inches Number of inches (Inches)
	 * @returns Total size (Inches)
	 */
	static joinFeetInch(feet: number, inches: number): number {
		return feet * INCHES_IN_FEET + inches;
	}

	/**
	 * Format a number into feet and inches
	 *
	 * @param value value to convert
	 * @param unitType Unit that value is in
	 * @returns Value formatted in feet and inches (1' 6")
	 */
	static formatFeetInch(value: number, unitType: IkeUnitType) {
		var inches = Math.round(IkeUnit.convert(value, IkeUnit.INCH, IkeUnit.FEET, unitType));
		var feetInches = this.splitFeetInch(inches);

		var display = `${Math.abs(feetInches.feet)}'`;

		if (value < 0) {
			display = `-${display}`;
		}

		if (feetInches.inches !== 0) {
			return `${display} ${Math.abs(feetInches.inches) + IkeUnit.getDisplay(IkeUnit.INCH, unitType)}`;
		}

		return display;
	}

	/**
	 * Attempt to parse Feet/Inches from a string
	 *
	 * @returns Parsed value or NaN (Meters)
	 */
	static parseFeetInch(input: string): number {
		var matches = input.match(FEET_INCH);
		if (matches == null) {
			return NaN;
		}

		// Feet and inches
		var sign = matches[1] == null ? 1 : -1;
		var feet = parseFloat(matches[2]);
		var inches = matches[3] ? parseFloat(matches[3]) : 0;

		if (inches > 11) {
			return NaN;
		}

		var feetM = IkeUnit.convert(feet, IkeUnit.METER, IkeUnit.FEET);
		var inchM = IkeUnit.convert(inches, IkeUnit.METER, IkeUnit.INCH);

		return (feetM + inchM) * sign;
	}
}

// Load the unit definitions.
export const UNITS: UnitInfo[] = [
	{ unit: IkeUnitDistance.METER, conversion: 1, display: ['m'], label: 'Meters', type: IkeUnitType.DISTANCE },
	{
		unit: IkeUnitDistance.CENTIMETER,
		conversion: 0.01,
		label: 'Centimeters',
		display: ['cm'],
		type: IkeUnitType.DISTANCE,
	},
	{ unit: IkeUnitDistance.MILLIMETER, conversion: 0.001, display: ['mm'], type: IkeUnitType.DISTANCE },
	{
		unit: IkeUnitDistance.FEET,
		conversion: 0.3048,
		display: ["'", 'ft', 'feet'],
		label: 'Feet/Inches',
		format: (value: number, unitType: IkeUnitType) => IkeUnit.formatFeetInch(value, unitType),
		type: IkeUnitType.DISTANCE,
	},
	{
		unit: IkeUnitDistance.INCH,
		conversion: 0.0254,
		display: ['"', 'in'],
		label: 'Inches',
		type: IkeUnitType.DISTANCE,
	},

	// Angle
	{ unit: IkeUnitAngle.RADIAN, conversion: 1, display: ['rad'], type: IkeUnitType.ANGLE },
	{ unit: IkeUnitAngle.DEGREE, conversion: Math.PI / 180, display: ['°'], type: IkeUnitType.ANGLE },

	// Area
	// TODO Area should not really share the same unit name as distance.
	{ unit: IkeUnitDistance.METER, conversion: 1, display: ['m²'], type: IkeUnitType.AREA },
	{ unit: IkeUnitDistance.CENTIMETER, conversion: 0.0001, display: ['cm²'], type: IkeUnitType.AREA },
	{ unit: IkeUnitDistance.MILLIMETER, conversion: 1e-6, display: ['mm²'], type: IkeUnitType.AREA },
	{ unit: IkeUnitDistance.INCH, conversion: 0.00064516, display: ['in²'], type: IkeUnitType.AREA },
	{ unit: IkeUnitDistance.FEET, conversion: 0.09290304, display: ['ft²'], type: IkeUnitType.AREA },
];
UNITS.forEach((u) => IkeUnit.addUnit(u));
